import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "../lib/content";

const IP_PHOTO_DEFAULT =
  "https://customer-assets.emergentagent.com/job_inner-pulse-1/artifacts/6r4mh0hz_Inner%20Pulse%201.jpg";
const SANDRITA_PHOTO_DEFAULT =
  "https://customer-assets.emergentagent.com/job_inner-pulse-1/artifacts/g20ts9rg_Sandrita3.jpg";
const SANDRITA_LOGO =
  "https://customer-assets.emergentagent.com/job_inner-pulse-1/artifacts/pn018vb4_Logo%20Sound%20met3d.png";
const DOWNTEMPO_PHOTO_DEFAULT =
  "https://customer-assets.emergentagent.com/job_inner-pulse-1/artifacts/2srmsksw_Inner%20Pulse%20foto%20brillo%20morado%20editada%20%281%29.jpg";

export default function About() {
  const kicker = useContent("page.about.kicker", "/ 02 — ABOUT");
  const pageTitle = useContent("page.about.title", "THREE IDENTITIES ONE ESSENCE");
  const ipTitle = useContent("page.about.ip.title", "INNER PULSE");
  const sandritaTitle = useContent("page.about.sandrita.title", "SANDRITA");
  const dtTitle = useContent("page.about.downtempo.title", "INNER PULSE");

  const ipPhoto = useContent("about.ip.photo", IP_PHOTO_DEFAULT);
  const ipTags = useContent("about.ip.tags", "Techno · Hardgroove · Tek · Tribe").split("·").map((s) => s.trim());
  const ipManifest = useContent("about.ip.manifest", "");
  const ipCatalyst = useContent("about.ip.catalyst", "");
  const ipEvolution = useContent("about.ip.evolution", "");

  const sPhoto = useContent("about.sandrita.photo", SANDRITA_PHOTO_DEFAULT);
  const sTags = useContent("about.sandrita.tags", "Prog · Dark Prog · BushProg · Psychedelic").split("·").map((s) => s.trim());
  const sManifest = useContent("about.sandrita.manifest", "");
  const sOrigins = useContent("about.sandrita.origins", "");
  const sToday = useContent("about.sandrita.today", "");

  const dtPhoto = useContent("about.downtempo.photo", DOWNTEMPO_PHOTO_DEFAULT);
  const dtTags = useContent("about.downtempo.tags", "Down Tempo · Organic · Psychedelic · Ritualistic").split("·").map((s) => s.trim());
  const dtManifest = useContent("about.downtempo.manifest", "");

  const sceneText = useContent("about.scene", "");

  // Optional 4th persona — only shown if title is set
  const aka4Title = useContent("page.about.aka4.title", "");
  const aka4Photo = useContent("about.aka4.photo", "");
  const aka4Tags = useContent("about.aka4.tags", "");
  const aka4Manifest = useContent("about.aka4.manifest", "");
  const aka4Description = useContent("about.aka4.description", "");
  const aka4TagList = aka4Tags ? aka4Tags.split("·").map((s) => s.trim()).filter(Boolean) : [];

  return (
    <div data-testid="about-page" className="px-6 md:px-12 py-20">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16" data-testid="about-header">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-silver-400" />
            <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
              {kicker}
            </span>
          </div>
          <h1 className="font-title text-silver-glow text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[0.06em] whitespace-pre-line">
            {pageTitle}
          </h1>
        </div>

        {/* INNER PULSE PROFILE */}
        <section
          className="grid md:grid-cols-12 gap-10 md:gap-16 mb-28"
          data-testid="about-innerpulse"
        >
          <div className="md:col-span-5 flex flex-col gap-4">
            <div
              className="aspect-[4/5] bg-silver-900 hairline overflow-hidden"
              data-testid="about-portrait-ip"
            >
              <img
                src={ipPhoto}
                alt="Inner Pulse — live"
                className="w-full h-full object-cover"
                style={{ filter: "grayscale(80%) contrast(1.1) brightness(0.92)" }}
              />
            </div>
          </div>

          <div className="md:col-span-7 flex flex-col gap-8">
            {/* BIG title above the bio text */}
            <div data-testid="ip-id-card" className="flex flex-col gap-2">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-500">
                / IDENTITY
              </div>
              <h2 className="font-title text-silver-glow text-3xl md:text-4xl lg:text-5xl tracking-[0.08em] leading-[1.05]">
                {ipTitle}
              </h2>
              <div className="flex flex-wrap items-center gap-2 text-silver-400 font-title text-[0.62rem] md:text-[0.7rem] tracking-[0.28em] uppercase">
                {ipTags.map((t, i) => (
                  <React.Fragment key={i}>
                    <span>{t}</span>
                    {i < ipTags.length - 1 && <span className="text-silver-700">·</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div data-testid="ip-manifest">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-3">
                / MANIFEST
              </div>
              <p className="font-body text-silver-200 text-lg md:text-xl leading-relaxed tracking-wide">
                {ipManifest || "Dancing between Hardgroove, Hypnotic, Obscure and more experimental territories of Techno, Inner Pulse shapes the moment, the space, and the energy of the dancefloor."}
              </p>
            </div>

            <div data-testid="ip-catalyst">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-3">
                / CATALYST
              </div>
              <p className="font-body text-silver-400 text-sm md:text-base leading-relaxed">
                {ipCatalyst || "Not only an artist but also a catalyst for the techno scene. Inner Pulse has been connecting people since 2020, promoting events in the southern region of Aotearoa, running Soundation series across different styles."}
              </p>
            </div>

            <div data-testid="ip-evolution">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-3">
                / EVOLUTION
              </div>
              <p className="font-body text-silver-400 text-sm md:text-base leading-relaxed">
                {ipEvolution || "Her sound keeps evolving while holding a clear essence."}
              </p>
            </div>
          </div>
        </section>

        {/* DIVIDER */}
        <div
          className="flex items-center gap-6 hairline-t hairline-b py-6 my-16"
          data-testid="about-divider-1"
        >
          <span className="font-title text-[0.62rem] tracking-[0.4em] text-silver-500">
            ALSO KNOWN AS
          </span>
          <div className="flex-1 h-px bg-gradient-to-r from-silver-500 via-silver-700 to-transparent" />
        </div>

        {/* SANDRITA PROFILE */}
        <section
          className="grid md:grid-cols-12 gap-10 md:gap-16 mb-24"
          data-testid="about-sandrita"
        >
          <div className="md:col-span-7 order-2 md:order-1 flex flex-col gap-8">
            {/* BIG title above the bio text */}
            <div data-testid="sandrita-id-card" className="flex flex-col gap-2">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-500">
                / AKA
              </div>
              <div className="flex items-center gap-4">
                <h2 className="font-title text-silver-glow text-3xl md:text-4xl lg:text-5xl tracking-[0.08em] leading-[1.05]">
                  {sandritaTitle}
                </h2>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-silver-400 font-title text-[0.62rem] md:text-[0.7rem] tracking-[0.28em] uppercase">
                {sTags.map((t, i) => (
                  <React.Fragment key={i}>
                    <span>{t}</span>
                    {i < sTags.length - 1 && <span className="text-silver-700">·</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div data-testid="sandrita-manifest">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-3">
                / MANIFEST
              </div>
              <p className="font-body text-silver-200 text-lg md:text-xl leading-relaxed tracking-wide">
                {sManifest || "Originally from Spain, influenced by the unique psychedelic flavor of the local New Zealand scene."}
              </p>
            </div>

            <div data-testid="sandrita-origins">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-3">
                / ORIGINS
              </div>
              <p className="font-body text-silver-400 text-sm md:text-base leading-relaxed">
                {sOrigins || "Born in Collado Villalba — Madrid, one of the European hubs of techno."}
              </p>
            </div>

            <div data-testid="sandrita-today">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-3">
                / TODAY
              </div>
              <p className="font-body text-silver-400 text-sm md:text-base leading-relaxed">
                {sToday || "Adaptive music with a heart at 140 BPM hardgroove."}
              </p>
            </div>
          </div>

          <div className="md:col-span-5 order-1 md:order-2 flex flex-col gap-4">
            <div
              className="aspect-[4/5] bg-silver-900 hairline overflow-hidden"
              data-testid="about-portrait-sandrita"
            >
              <img
                src={sPhoto}
                alt="Sandrita portrait"
                className="w-full h-full object-cover"
                style={{ filter: "contrast(1.05)" }}
              />
            </div>
          </div>
        </section>

        {/* DIVIDER 2 */}
        <div
          className="flex items-center gap-6 hairline-t hairline-b py-6 my-16"
          data-testid="about-divider-2"
        >
          <span className="font-title text-[0.62rem] tracking-[0.4em] text-silver-500">
            DEEPER LAYER
          </span>
          <div className="flex-1 h-px bg-gradient-to-r from-silver-500 via-silver-700 to-transparent" />
        </div>

        {/* DOWNTEMPO / ORGANIC PROFILE */}
        <section
          className="grid md:grid-cols-12 gap-10 md:gap-16 mb-24"
          data-testid="about-downtempo"
        >
          <div className="md:col-span-5 flex flex-col gap-4">
            <div
              className="aspect-[4/5] bg-silver-900 hairline overflow-hidden"
              data-testid="about-portrait-downtempo"
            >
              <img
                src={dtPhoto}
                alt="Inner Pulse — Down tempo · Organic"
                className="w-full h-full object-cover"
                style={{ filter: "contrast(1.08) saturate(1.05)" }}
              />
            </div>
          </div>

          <div className="md:col-span-7 flex flex-col gap-8">
            {/* BIG title above the bio text */}
            <div data-testid="downtempo-id-card" className="flex flex-col gap-2">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-500">
                / DEEPER LAYER
              </div>
              <h2 className="font-title text-silver-glow text-3xl md:text-4xl lg:text-5xl tracking-[0.08em] leading-[1.05]">
                {dtTitle}
              </h2>
              <div className="flex flex-wrap items-center gap-2 text-silver-400 font-title text-[0.62rem] md:text-[0.7rem] tracking-[0.28em] uppercase">
                {dtTags.map((t, i) => (
                  <React.Fragment key={i}>
                    <span>{t}</span>
                    {i < dtTags.length - 1 && <span className="text-silver-700">·</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div data-testid="downtempo-manifest">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-3">
                / MANIFEST
              </div>
              <p className="font-body text-silver-200 text-lg md:text-xl leading-relaxed tracking-wide">
                {dtManifest || "There is a different tempo, a different energy for every moment."}
              </p>
            </div>
          </div>
        </section>

        {/* OPTIONAL 4th PERSONA — visible only when title is set */}
        {aka4Title && (
          <section
            className="grid md:grid-cols-12 gap-10 md:gap-16 mb-24"
            data-testid="about-aka4"
          >
            <div className="md:col-span-5 flex flex-col gap-4">
              {aka4Photo && (
                <div
                  className="aspect-[4/5] bg-silver-900 hairline overflow-hidden"
                  data-testid="about-portrait-aka4"
                >
                  <img
                    src={aka4Photo}
                    alt={aka4Title}
                    className="w-full h-full object-cover"
                    style={{ filter: "contrast(1.08) saturate(1.05)" }}
                  />
                </div>
              )}
            </div>
            <div className="md:col-span-7 flex flex-col gap-8">
              <div className="flex flex-col gap-2">
                <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-500">
                  / FOURTH IDENTITY
                </div>
                <h2 className="font-title text-silver-glow text-3xl md:text-4xl lg:text-5xl tracking-[0.08em] leading-[1.05]">
                  {aka4Title}
                </h2>
                {aka4TagList.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 text-silver-400 font-title text-[0.62rem] md:text-[0.7rem] tracking-[0.28em] uppercase">
                    {aka4TagList.map((t, i) => (
                      <React.Fragment key={i}>
                        <span>{t}</span>
                        {i < aka4TagList.length - 1 && <span className="text-silver-700">·</span>}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              {aka4Manifest && (
                <div>
                  <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-3">
                    / MANIFEST
                  </div>
                  <p className="font-body text-silver-200 text-lg md:text-xl leading-relaxed tracking-wide">
                    {aka4Manifest}
                  </p>
                </div>
              )}
              {aka4Description && (
                <p className="font-body text-silver-400 text-sm md:text-base leading-relaxed">
                  {aka4Description}
                </p>
              )}
            </div>
          </section>
        )}

        {/* SCENE + LINKS */}
        <section
          className="hairline-t pt-10 grid md:grid-cols-2 gap-10"
          data-testid="about-scene"
        >
          <div>
            <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-4">
              / TECHNO SCENE — AOTEAROA
            </div>
            <p className="font-body text-silver-400 text-sm leading-relaxed">
              {sceneText || "Working on supporting the Techno scene grow in the South Island of New Zealand since 2020."}
            </p>
          </div>
          <div>
            <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-4">
              / SOUNDATION SERIES
            </div>
            <ul className="font-body text-silver-300 text-sm space-y-2">
              <li>— <span className="text-silver-500">TechMeOver:</span> TechHouse · Minimal · Techno</li>
              <li>— <span className="text-silver-500">Suave:</span> Deep House · Organic downtempo · Analog</li>
              <li>— <span className="text-silver-500">SŌMA:</span> Hardgroove · Hypnotic · Tribe · Ritualistic</li>
              <li>— <span className="text-silver-500">Soundation Festival:</span> Valley Views Glamping · NZ</li>
              <li>— <span className="text-silver-500">Pulse Stage:</span> Yatra Festival — Takaka, NZ</li>
            </ul>
          </div>
        </section>

        <div className="flex gap-6 flex-wrap mt-14" data-testid="about-ctas">
          <Link to="/sets" className="btn-sharp" data-testid="about-cta-sets">
            Listen
          </Link>
          <Link to="/community" className="btn-ghost" data-testid="about-cta-community">
            Community <ArrowUpRight size={14} />
          </Link>
          <Link to="/booking" className="btn-ghost" data-testid="about-cta-booking">
            Booking <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
